import { HStack, Box } from '@bpba/ui-components';
import { useNavigate } from 'react-router-dom';
import { MenuItem } from './MenuItem';
import styled from 'styled-components';
import { useEffect, useState, useRef } from 'react';
import { UrlsFront } from '../../constants/urls';
import { useMenuContext } from '../useMenuContext';
import { MENU_ITEM_LIST, MENU_ITEM_NEW } from '../../constants/data';

export const Menu = () => {
  const { getOption } = useMenuContext();
  const [selected, setSelected] = useState<number>(getOption());
  const navigate = useNavigate();

  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const gapToScroll = 200;

  useEffect(() => {
    setSelected(getOption);
  }, [getOption]);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;

    setShowLeft(el.scrollLeft > 0);
    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    setShowRight(el.scrollLeft < maxScrollLeft - 1);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    checkScroll();
    const onScroll = () => checkScroll();
    el.addEventListener('scroll', onScroll);
    window.addEventListener('resize', checkScroll);

    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;

    const handleMouseDown = (e: MouseEvent) => {
      isDown = true;
      el.classList.add('dragging');
      startX = e.pageX - el.offsetLeft;
      scrollLeft = el.scrollLeft;
    };

    const handleMouseLeave = () => {
      isDown = false;
      el.classList.remove('dragging');
    };

    const handleMouseUp = () => {
      isDown = false;
      el.classList.remove('dragging');
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - el.offsetLeft;
      const walk = (x - startX) * 1;
      el.scrollLeft = scrollLeft - walk;
    };

    el.addEventListener('mousedown', handleMouseDown);
    el.addEventListener('mouseleave', handleMouseLeave);
    el.addEventListener('mouseup', handleMouseUp);
    el.addEventListener('mousemove', handleMouseMove);

    // Touch support
    let startTouchX = 0;
    let startScrollLeft = 0;

    const handleTouchStart = (e: TouchEvent) => {
      startTouchX = e.touches[0].pageX;
      startScrollLeft = el.scrollLeft;
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touchX = e.touches[0].pageX;
      const walk = touchX - startTouchX;
      el.scrollLeft = startScrollLeft - walk;
    };

    el.addEventListener('touchstart', handleTouchStart);
    el.addEventListener('touchmove', handleTouchMove);

    return () => {
      el.removeEventListener('mousedown', handleMouseDown);
      el.removeEventListener('mouseleave', handleMouseLeave);
      el.removeEventListener('mouseup', handleMouseUp);
      el.removeEventListener('mousemove', handleMouseMove);
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  const scrollBy = (amount: number) => {
    const el = scrollRef.current;
    if (!el) return;

    const maxScrollLeft = el.scrollWidth - el.clientWidth;

    if (amount > 0) {
      const remaining = maxScrollLeft - el.scrollLeft;
      el.scrollTo({
        left: el.scrollLeft + (remaining < gapToScroll ? remaining : gapToScroll),
        behavior: 'smooth',
      });
    } else {
      const remaining = el.scrollLeft;
      el.scrollTo({
        left: el.scrollLeft - (remaining < gapToScroll ? remaining : gapToScroll),
        behavior: 'smooth',
      });
    }
  };

  return (
    <Box>
      {showLeft && <ArrowButton left onClick={() => scrollBy(-gapToScroll)} />}
      <StyledNoScrollBarHstack ref={scrollRef} width="100%" scrollEnabled={showLeft || showRight}>
        <MenuItem
          onClick={() => {
            setSelected(1);
            navigate(UrlsFront.FixedTerms);
          }}
          selected={selected === 1}
        >
          {MENU_ITEM_LIST}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSelected(2);
            navigate(UrlsFront.FixedTermSimulation);
          }}
          selected={selected === 2}
        >
          {MENU_ITEM_NEW}
        </MenuItem>
      </StyledNoScrollBarHstack>
      {showRight && <ArrowButton right onClick={() => scrollBy(gapToScroll)} />}
    </Box>
  );
};

// Styled Components

const StyledNoScrollBarHstack = styled(HStack)<{ scrollEnabled?: boolean }>`
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
  gap: 16px;
  ${({ scrollEnabled }) => scrollEnabled && `cursor: grab;`}

  &.dragging {
    ${({ scrollEnabled }) => scrollEnabled && `cursor: grabbing;`}
  }
`;

const ArrowButton = styled.button<{ left?: boolean; right?: boolean }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  background: #ebebeb;
  border: none;
  padding: 12px;
  border-radius: 50%;
  box-shadow:
    0 1px 3px rgba(60, 64, 67, 0.3),
    0 4px 8px 3px rgba(60, 64, 67, 0.15);
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  ${({ left }) => left && `left: 8px;`}
  ${({ right }) => right && `right: 8px;`}

  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    border-style: solid;
    border-width: 2px 2px 0 0;
    transform: ${({ left }) => (left ? 'rotate(-135deg)' : 'rotate(45deg)')};
    border-color: rgb(39, 157, 46);
  }

  &:hover {
    opacity: 1 !important;
    background: #f2f2f2;
  }
`;
